import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';

import {
  getCollection,
  getFile,
  getFileUsers,
  updateFile,
  patchFileUsers,
} from 'api/market-view-api';
import type {
  CollectionResponse,
  AssetDto,
  UserAccessDto,
  UpdateFileArgs,
  AssetClientUserUpdateRequest,
  CurrentUserHolder,
} from 'api/market-view-api';
import type { SortDirection, FilterValue } from 'helpers/api/build-query-params';

export interface FileCollectionRecord extends AssetDto {
  asset: {
    name: string;
    fileName: string;
    fileExt: string;
    description?: string;
    createdDate?: string;
    [key: string]: unknown;
  };
  viewed?: boolean;
  isAvailableTo?: Array<{
    firstName?: string;
    lastName?: string;
    [key: string]: unknown;
  }>;
}

export const filesKeys = {
  all: ['files'] as const,
  lists: () => [...filesKeys.all, 'list'] as const,
  list: (params?: FilesListParams) =>
    [...filesKeys.lists(), params] as const,
  detail: (clientId: string | number, fileId: string | number) =>
    [...filesKeys.all, 'detail', clientId, fileId] as const,
  users: (clientId: string | number, fileId: string | number) =>
    [...filesKeys.all, 'users', clientId, fileId] as const,
};

export interface FilesListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
}

interface UseFilesListArgs extends FilesListParams {
  enabled?: boolean;
}

export const useFilesList = ({
  pageSize = 10,
  pageIndex = 0,
  sortBy = [],
  filters,
  enabled = true,
}: UseFilesListArgs): UseQueryResult<
  CollectionResponse<FileCollectionRecord[]>,
  Error
> =>
  useQuery({
    queryKey: filesKeys.list({ pageSize, pageIndex, sortBy, filters }),
    queryFn: async () => {
      const response = await getCollection('files', {
        pageSize,
        pageIndex,
        sortBy,
        filters,
      });

      return response as CollectionResponse<FileCollectionRecord[]>;
    },
    enabled,
    placeholderData: (previousData) => previousData,
  });

// --- Single file detail hooks ---

interface UseFileArgs {
  userId: string;
  clientId: string | number;
  fileId: string | number;
  enabled?: boolean;
}

export const useFile = ({
  userId,
  clientId,
  fileId,
  enabled = true,
}: UseFileArgs): UseQueryResult<AssetDto, Error> =>
  useQuery({
    queryKey: filesKeys.detail(clientId, fileId),
    queryFn: async () => {
      const response = await getFile({ userId, clientId, fileId });

      return response.data;
    },
    enabled: enabled && !!userId && !!clientId && !!fileId,
  });

export const useFileUsers = ({
  userId,
  clientId,
  fileId,
  enabled = true,
}: UseFileArgs): UseQueryResult<UserAccessDto[], Error> =>
  useQuery({
    queryKey: filesKeys.users(clientId, fileId),
    queryFn: async () => {
      const response = await getFileUsers({ userId, clientId, fileId });

      return response.data;
    },
    enabled: enabled && !!userId && !!clientId && !!fileId,
  });

export const useUpdateFile = (): UseMutationResult<
  AssetDto,
  Error,
  UpdateFileArgs
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: UpdateFileArgs) => {
      const response = await updateFile(args);

      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: filesKeys.detail(variables.clientId, variables.fileId),
      });
      queryClient.invalidateQueries({ queryKey: filesKeys.lists() });
    },
  });
};

interface PatchFileUsersVariables {
  currentUser: CurrentUserHolder;
  clientId: string | number;
  fileId: string | number;
  changedUsers: AssetClientUserUpdateRequest[];
}

export const usePatchFileUsers = (): UseMutationResult<
  UserAccessDto[],
  Error,
  PatchFileUsersVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: PatchFileUsersVariables) => {
      const response = await patchFileUsers(args);

      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: filesKeys.users(variables.clientId, variables.fileId),
      });
    },
  });
};
